import React, { useState, useEffect } from "react";
import { t } from "i18next";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { Button, Col, Result, Row, Spin } from "common/elements";
import { getPayslipById, getFinalPaymentById } from "services/payroll";
import "./payslip.css";
import { Util } from "common/util";
import Enum from "common/enums";
import { getKhmerDate, getKhmerMonth, getKhmerYear } from "./helper";
// import { Util } from "common/util";

const ViewPayslip = () => {
  const [payslip, setPayslip] = useState({
    employee: {},
    netPay: 0,
    payrollEntries: [],
    netPayEntries: [],
  });
  const [loading, setLoading] = useState(false);
  const [notFoundResult, setNotFoundResult] = useState(false);
  const params = new URLSearchParams(document.location.search);

  const navigate = useNavigate();
  const { id } = useParams();

  // eslint-disable-next-line
  const isProceedFinalPay = params.get("isProceedFinalPay") == 1;

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = isProceedFinalPay ? `@media print {
         @page {
            margin-left: 5mm;
            margin-right: 1mm;
         }

         .ant-layout-header {
            display: none !important
         }

         #main-content-layout {
            margin: 0px !important
         }

         #payslip{
            width: 100%;
            margin: 0 auto;
            height: 800mm;
          }

         .scroll {
            height: auto;
            padding: 0
         }
      }`: `@media print {
         @page {
            margin-left: 5mm;
            margin-right: 5mm;
         }

         .ant-layout-header {
            display: none !important
         }

         #main-content-layout {
            margin: 0px !important
         }

         #payslip{
            width: 100%;
            margin: 0 auto;
            height: 400mm;
          }

         .scroll {
            height: auto;
            padding: 0
         }
      }`;

    document.head.appendChild(style);

    setLoading(true);
    let promise = null;

    if (isProceedFinalPay) {
      promise = getFinalPaymentById(id);
    } else {
      promise = getPayslipById(id);
    }

    promise
      .then((response) => {
        console.log(response)
        if (response.data) {
          let { payrollEntries } = response.data;
          const foundFirstPaymentAt = payrollEntries.findIndex(
            (payrollEntry) => payrollEntry.payrollItemCode === "first_payment"
          );
          const netPayEntries = payrollEntries.slice(foundFirstPaymentAt);
          payrollEntries = payrollEntries.slice(0, foundFirstPaymentAt);

          setPayslip(() => ({
            ...response.data,
            payrollEntries,
            netPayEntries,
          }));
        }
      })
      .catch((error) => {
        if (error.response.status === 404) {
          setNotFoundResult(true);
        }
      })
      .finally(() => setLoading(false));

    // eslint-disable-next-line
  }, []);

  const onBack = () => {
    navigate("/payrolls");
  };

  const onPrint = () => {
    window.print();
  };

  if (loading) return <Spin spinning={loading} className="loading-view" />;

  return notFoundResult ? (
    <Result
      status="404"
      title="404"
      subTitle="Sorry, the page you visited does not exist."
      extra={
        <Button type="primary" onClick={onBack}>
          Back
        </Button>
      }
    />
  ) : (
    <>
      <div className="btn-navigation" id="non-printable">
        <Row>
          <Col span={12}>
            <div className="pull-left">
              <Button onClick={onBack}>
                <ArrowLeftOutlined />
                {t("text_back")}
              </Button>
              <span className="title">
                {isProceedFinalPay ? "Final Payment" : "Payslip"}
              </span>
            </div>
          </Col>
          <Col span={12}>
            <div className="pull-right">
              <Button type="primary" onClick={onPrint}>
                <PrinterOutlined />
                {t("text_print")}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
      {isProceedFinalPay ? (
        <FinalPay payslip={payslip} />
      ) : (
        <Payslip payslip={payslip} />
      )}
    </>
  );
};

const isDeductionAmount = (payrollEntry) => {
  return [
    Enum.PAYROLL_ITEM_TYPE.DEDUCTION,
    Enum.PAYROLL_ITEM_TYPE.TAXABLE_DEDUCTION,
    Enum.PAYROLL_ITEM_TYPE.PENSION_FUND,
  ].includes(payrollEntry.type);
};

function Payslip({ payslip }) {
  const payrollElement = (payrollEntry, index) => {
    let payrollEntryRow = (
      <tr key={index}>
        <td className="width-colcumns sub-title" colSpan={3}>
          {payrollEntry.title}&nbsp;&nbsp;&nbsp;
          <span className="khmer-text">{payrollEntry.titleKH}</span>
        </td>
        {isDeductionAmount(payrollEntry) ? (
          <td
            className="width-columns-setion-2"
            style={{ textAlign: "right", color: "red" }}
          >
            (
            {payrollEntry.code === "spouse" || payrollEntry.code === "kid"
              ? payrollEntry.amount
              : Util.getInstance().formatCurrency(payrollEntry.amount, "$")}
            )
          </td>
        ) : (
          <td className="width-columns-setion-2" style={{ textAlign: "right" }}>
            {payrollEntry.code === "spouse" || payrollEntry.code === "kid"
              ? payrollEntry.amount
              : Util.getInstance().formatCurrency(payrollEntry.amount, "$")}
          </td>
        )}
      </tr>
    );
    if (payrollEntry.code === "gross_salary") {
      payrollEntryRow = (
        <tr className="text-total-netpay">
          <td className="width-colcumns sub-title" colSpan={3}>
            {payrollEntry.title}
          </td>
          {isDeductionAmount(payrollEntry) ? (
            <td
              className="width-columns-setion-2"
              style={{ textAlign: "right", color: "red" }}
            >
              ({Util.getInstance().formatCurrency(payrollEntry.amount, "$")})
            </td>
          ) : (
            <td
              className="width-columns-setion-2"
              style={{ textAlign: "right" }}
            >
              {Util.getInstance().formatCurrency(payrollEntry.amount, "$")}
            </td>
          )}
        </tr>
      );
    }
    return payrollEntryRow;
  };

  return (
    <div className="display-table-view scroll" id="payslip">
      <section className="section-header">
        <div className="header-title">
          <img src="/logo.png" alt="logo" />
          <div className="title-info">
            <h3>Norwegain People's Aid</h3>
            <h5>Cambodia</h5>
          </div>
        </div>
      </section>
      <div className="date-payslip">
        <p
          className="khmer-text-bold"
          style={{ fontSize: 18, marginBottom: 2 }}
        >
          បង្កាន់ដៃបើកប្រាក់បៀវត្ស
        </p>
        <div className="khmer-text-bold" style={{ fontSize: 18 }}>
          ប្រចាំខែ{" "}
          <span>{`${
            payslip.month &&
            (payslip.month.toString().length === 1
              ? getKhmerMonth(`1970-0${payslip.month}-01`)
              : getKhmerMonth(`1970-${payslip.month}-01`))
          } ឆ្នាំ ${
            payslip.year && getKhmerYear(`${payslip.year}-01-01`)
          }`}</span>
        </div>
        <h2 style={{ fontWeight: "bold", marginBottom: 2 }}>MONTHLY PAYSLIP</h2>
        <div>
          For{" "}
          <span>{`${
            payslip.month &&
            (payslip.month.toString().length === 1
              ? moment(`1970-0${payslip.month}-01`).format("MMMM")
              : moment(`1970-${payslip.month}-01`).format("MMMM"))
          } ${payslip.year && payslip.year}`}</span>
        </div>
      </div>

      <section className="section-table">
        <table id="table1">
          <thead>
            <tr>
              <th colSpan={4} className="bg-header-table">
                <span className="title">
                  EMPLOYEE’S INFORMATION:&nbsp;&nbsp;&nbsp;
                  <span className="khmer-text">ព័ត៍មាននិយោជិត</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="setion-1">
            <tr>
              <td className="width-colcumns sub-title">
                Name of Staff&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">ព័ត៍មាននិយោជិត</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.employeeName}
              </td>
            </tr>
            <tr>
              <td className="width-colcumns sub-title">
                Position Title&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">តួនាទី</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.position}
              </td>
            </tr>
            <tr>
              <td className="width-colcumns sub-title">
                Bank Account&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">គណនីធនាគារ</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.bankAccNumber}
              </td>
            </tr>
            <tr>
              <td className="width-colcumns sub-title">
                No. of dependent&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">អ្នកអាស្រ័យ</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.numberOfdependent}
              </td>
            </tr>
          </tbody>
          <thead>
            <tr>
              <th colSpan={4} className="bg-header-table">
                <span className="title">
                  PAYROLL INFORMATION:&nbsp;&nbsp;&nbsp;
                  <span className="khmer-text">
                    ព័ត៍មានអំពីប្រាក់ឈ្នួល​ និង ប្រាក់ឧបត្ថម្ភនានា
                  </span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="setion-2">
            <tr>
              <td className="width-colcumns sub-title" colSpan={3}>
                Basic Salary&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">ប្រាក់ខែគោល</span>
              </td>
              <td
                className="width-columns-setion-2"
                style={{ textAlign: "right" }}
              >
                {Util.getInstance().formatCurrency(payslip.basicSalary, "$")}
              </td>
            </tr>
            {payslip.payrollEntries.map((payrollEntry, index) =>
              payrollElement(payrollEntry, index)
            )}
            <tr className="text-total-netpay">
              <td className="width-colcumns sub-title" colSpan={3}>
                TOTAL NET PAYMENT&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">ប្រាក់បៀវត្សដែលទទួលបានសរុប</span>
              </td>
              <td
                className="width-columns-setion-2"
                style={{ textAlign: "right" }}
              >
                {Util.getInstance().formatCurrency(payslip.netPay, "$")}
              </td>
            </tr>
            <tr className="text-total-netpay">
              <td className="width-colcumns sub-title" colSpan={3}>
                Uptodate Provident Fund - Staff and NPA&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">សមតុល្យមូលនិធិបម្រុងទុក កាត់ទុកពីបុគ្គលិករួមទាំងអិនភីអេ</span>
              </td>
              <td
                className="width-columns-setion-2"
                style={{ textAlign: "right" }}
              >
                {Util.getInstance().formatCurrency(payslip.balancePFOrganization + payslip.balancePFStaff, "$")}
              </td>
            </tr>
            {payslip.netPayEntries.map((payrollEntry, index) =>
              payrollElement(payrollEntry, index)
            )}
          </tbody>
        </table>
        <table id="table2">
          <tbody>
            <tr>
              <td className="header-text-Italic">
                Acknowledgment of Staff:&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">ការទទួលស្គាល់របស់បុគ្គលិក:</span>
              </td>
            </tr>
            <tr>
              <td className="text-grey" style={{ fontWeight: "normal" }}>
                I hereby acknowledge the receipt of the total net pay of{" "}
                <strong style={{ color: "black" }}>
                  US {Util.getInstance().formatCurrency(payslip.netPay)}
                </strong>{" "}
                which got deposited to my ABA bank account, as stated above. If
                I come across any error in this pays lip, instantly contact HRD
                unit.&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">
                  ខ្ញុំបានទទួលស្គាល់ការទទួលប្រាក់បៀវត្សរ៍សរុបចំនួន US{" "}
                  {Util.getInstance().formatCurrency(payslip.netPay)}{" "}
                  ដែលបានបញ្ចូលទៅក្នុងគណនីធនាគារ ABA របស់ខ្ញុំ ដូចមានចែងខាងលើ។
                  ប្រសិនបើខ្ញុំជួបបញ្ហាណាមួយក្នុងការទទួលប្រាក់នេះ
                  ខ្ញុំនឹងទាក់ទងអង្គភាព HRD យ៉ាងភ្លាមៗ។
                </span>
              </td>
            </tr>
            <tr>
              <td className="text-grey" style={{ fontWeight: "normal" }}>
                Remarks: not require returning a signed copy of this payslip to
                NPA.&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">
                  កំណត់សម្គាល់​​​​៖
                  មិនតម្រូវឲប្រគាល់ច្បាប់ចម្លងដែលមានហត្ថលេខានៃបង្កាន់ដៃបើកប្រាក់បៀវត្សរ៍នេះទៅ
                  NPAទេ។
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="footer">
        <div className="section-footer">
          <ul className="sec-1">
            <li>
              Phnom Penh Office #4, St. 278, Sangkat Olympic, P.O.Box 2228,Phnom
              Penh, Cambodia.
            </li>
            <li>
              Phone : <span>855-23 210 383, 855-23 218 197</span>
            </li>
            <li>
              Mobile : <span>855-12 800 103, 855-12 817 217</span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}

function FinalPay({ payslip }) {
  const payrollElement = (payrollEntry, index) => {
    let payrollEntryRow = (
      <tr
        key={index}
        className={
          payrollEntry.payrollItemCode === "gross_salary"
            ? "text-total-netpay"
            : ""
        }
      >
        <td className="width-colcumns sub-title">
          {payrollEntry.title}&nbsp;&nbsp;&nbsp;
          <span className="khmer-text">{payrollEntry.titleKH}</span>
        </td>
        {isDeductionAmount(payrollEntry) ? (
          <td
            className="width-columns-setion-2"
            style={{ textAlign: "right", color: "red" }}
          >
            (
            {payrollEntry.payrollItemCode === "spouse" ||
            payrollEntry.code === "kid"
              ? payrollEntry.amount
              : Util.getInstance().formatCurrency(payrollEntry.amount, "$")}
            )
          </td>
        ) : (
          <td className="width-columns-setion-2" style={{ textAlign: "right" }}>
            {payrollEntry.payrollItemCode === "spouse" ||
            payrollEntry.code === "kid"
              ? payrollEntry.amount
              : Util.getInstance().formatCurrency(payrollEntry.amount, "$")}
          </td>
        )}

        {payrollEntry.payrollItemCode === "second_payment" ? (
          <td className="width-columns-setion-2">
            <p>
              The total amount (13th month salary, annual leave) will be settled
              and transferred to your bank account on{" "}
              {moment(payslip.to).format("D MMM YYYY")} together with the second
              payment for all staff.
            </p>
            <p className="khmer-text">
              ចំនួនទឹកប្រាក់សរុប (ប្រាក់ខែទី 13,​ ប្រាក់ខែ,
              ការឈប់សម្រាកប្រចាំឆ្នាំ) នឹងត្រូវបានទូទាត់
              និងផ្ទេរទៅគណនីធនាគាររបស់អ្នកនៅថ្ងៃទី​ {getKhmerDate(payslip.to)}{" "}
              ខែ​ {getKhmerMonth(payslip.to)} ឆ្នាំ {getKhmerYear(payslip.to)}{" "}
              រួមជាមួយនឹងការទូទាត់ប្រាក់លើកទីពីរសម្រាប់បុគ្គលិកទាំងអស់។
            </p>
          </td>
        ) : (
          <td className="width-columns-setion-2">{payrollEntry?.remark}</td>
        )}
      </tr>
    );

    return payrollEntryRow;
  };

  return (
    <div className="display-table-view scroll" id="payslip">
      <section className="section-header">
        <div className="header-title">
          <img src="/logo.png" alt="logo" />
          <div className="title-info">
            <h3>Norwegain People's Aid</h3>
            <h5>Cambodia</h5>
          </div>
        </div>
      </section>
      <div className="date-payslip">
        <p
          className="khmer-text-bold"
          style={{ fontSize: 18, marginBottom: 2 }}
        >
          ការទូទាត់ចុងក្រោយ
        </p>
        <p
          className="khmer-text-bold"
          style={{ fontSize: 18, marginBottom: 2 }}
        >
          ប្រាក់បៀវត្ស និងអត្ថប្រយោជន៍ផ្សេងៗ
        </p>
        <div className="khmer-text-bold" style={{ fontSize: 18 }}>
          ប្រចាំខែ{" "}
          <span>{`${
            payslip.month &&
            (payslip.month.toString().length === 1
              ? getKhmerMonth(`1970-0${payslip.month}-01`)
              : getKhmerMonth(`1970-${payslip.month}-01`))
          } ឆ្នាំ ${
            payslip.year && getKhmerYear(`${payslip.year}-01-01`)
          }`}</span>
        </div>
        <h2 style={{ marginBottom: 0, fontWeight: "bold" }}>FINAL PAYMENT</h2>
        <div>
          For{" "}
          <span>{`${
            payslip.month &&
            (payslip.month.toString().length === 1
              ? moment(`1970-0${payslip.month}-01`).format("MMMM")
              : moment(`1970-${payslip.month}-01`).format("MMMM"))
          } ${payslip.year && payslip.year}`}</span>
        </div>
      </div>

      <section className="section-table" style={{ marginTop: 25 }}>
        <table id="table1">
          <thead>
            <tr className="bg-header-table">
              <th colSpan={4}>
                <span className="title">
                  EMPLOYEE’S INFORMATION:&nbsp;&nbsp;&nbsp;
                  <span className="khmer-text">ព័ត៍មាននិយោជិត</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="setion-1">
            <tr>
              <td className="width-colcumns sub-title">
                Name of Staff&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">ព័ត៍មាននិយោជិត</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.employeeName}
              </td>
            </tr>
            <tr>
              <td className="width-colcumns sub-title">
                Position Title&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">តួនាទី</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.position}
              </td>
            </tr>
            <tr>
              <td className="width-colcumns sub-title">
                Bank Account&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">គណនីធនាគារ</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.bankAccNumber}
              </td>
            </tr>
            <tr>
              <td className="width-colcumns sub-title">
                No. of dependent&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">អ្នកអាស្រ័យ</span>
              </td>
              <td colSpan={3} style={{ color: "red" }}>
                {payslip?.numberOfdependent}
              </td>
            </tr>
          </tbody>
          <thead>
            <tr className="bg-header-table">
              <th>
                <span className="title">
                  PAYROLL INFORMATION:&nbsp;&nbsp;&nbsp;
                  <span className="khmer-text">
                    ព័ត៍មានអំពីប្រាក់ឈ្នួល​ និង ប្រាក់ឧបត្ថម្ភនានា
                  </span>
                </span>
              </th>
              <th>
                <span className="title">AMOUNT:&nbsp;&nbsp;&nbsp;
                  <span className="khmer-text">
                    ពចំនួនទឹកប្រាក់
                  </span>
                </span>
              </th>
              <th>
                <span className="title">REMARK:&nbsp;&nbsp;&nbsp;
                  <span className="khmer-text">
                   ចំណាំ
                  </span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="setion-2">
          <tr>
            <td className="width-colcumns sub-title">Basic Salary</td>
            <td
                className="width-columns-setion-2"
                style={{textAlign: "right"}}
            >
              {Util.getInstance().formatCurrency(payslip.basicSalary, "$")}
            </td>
            <td className="width-columns-setion-2"></td>
          </tr>
          {payslip.payrollEntries.map((payrollEntry, index) =>
              payrollElement(payrollEntry, index)
          )}
          {payslip.netPayEntries.map((payrollEntry, index) =>
              payrollElement(payrollEntry, index)
          )}
          <tr className="text-total-netpay">
            <td className="width-colcumns sub-title">
              TOTAL NET PAYMENT&nbsp;&nbsp;&nbsp;
              <span className="khmer-text">ប្រាក់បៀវត្សដែលទទួលបានសរុប</span>
            </td>
            <td className="width-columns-setion-2" style={{textAlign: "right"}}>
              {Util.getInstance().formatCurrency(payslip.netPay, "$")}
            </td>
            <td className="width-columns-setion-2"></td>
          </tr>
          <tr className="text-total-netpay">
            <td className="width-colcumns sub-title">
              Uptodate Provident Fund - Staff and NPA&nbsp;&nbsp;&nbsp;
              <span className="khmer-text">សមតុល្យមូលនិធិបម្រុងទុក កាត់ទុកពីបុគ្គលិករួមទាំងអិនភីអេ</span>
            </td>
            <td className="width-columns-setion-2" style={{textAlign: "right"}}>
              {Util.getInstance().formatCurrency(payslip.balancePFOrganization + payslip.balancePFStaff, "$")}
            </td>
            <td className="width-columns-setion-2"></td>
          </tr>
          </tbody>
        </table>
        <table id="table2">
          <tbody>
          <tr>
            <td style={{border: "none"}}>
              <div style={{marginTop: 25}}>Signatures: ហត្ថលេខា</div>
              <div
                  style={{
                    width: 250,
                    borderBottom: "1px solid black",
                    marginTop: 30,
                  }}
              />
              <div
                  style={{
                    marginTop: 15,
                    marginBottom: 25,
                    fontWeight: "bold",
                  }}
                >
                  {payslip?.employeeName}
                </div>
              </td>
            </tr>
            <tr>
              <td className="text-grey" style={{ fontWeight: "normal" }}>
                The total outstanding amount of Provident Fund (PF) will be
                transferred to your ABA bank account on{" "}
                {moment(payslip.to).format("D MMM YYYY")}.&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">
                  ការទូទាត់សមតុល្យមូលនិធិបម្រុងទុកសរុបនឹងត្រូវបានបញ្ជូនទៅគណនីធនាគារ
                  ABA របស់អ្នកនៅថ្ងៃទី​ {getKhmerDate(payslip.to)} ខែ​{" "}
                  {getKhmerMonth(payslip.to)} ឆ្នាំ {getKhmerYear(payslip.to)}។
                </span>
              </td>
            </tr>
            <tr>
              <td className="header-text-Italic">
                Acknowledgment of Staff:&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">ការទទួលស្គាល់របស់បុគ្គលិក:</span>
              </td>
            </tr>
            <tr>
              <td className="text-grey" style={{ fontWeight: "normal" }}>
                I hereby acknowledge the receipt of the total net pay of{" "}
                <strong style={{ color: "black" }}>
                  US {Util.getInstance().formatCurrency(payslip.netPay)}
                </strong>{" "}
                which got deposited to my ABA bank account, as stated above. If
                I come across any error in this pays lip, instantly contact HRD
                unit.&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">
                  ខ្ញុំបានទទួលស្គាល់ការទទួលប្រាក់បៀវត្សរ៍សរុបចំនួន US{" "}
                  {Util.getInstance().formatCurrency(payslip.netPay)}{" "}
                  ដែលបានបញ្ចូលទៅក្នុងគណនីធនាគារ ABA របស់ខ្ញុំ ដូចមានចែងខាងលើ។
                  ប្រសិនបើខ្ញុំជួបបញ្ហាណាមួយក្នុងការទទួលប្រាក់នេះ
                  ខ្ញុំនឹងទាក់ទងអង្គភាព HRD យ៉ាងភ្លាមៗ។
                </span>
              </td>
            </tr>
            <tr>
              <td className="text-grey" style={{ fontWeight: "normal" }}>
                Remarks: not require returning a signed copy of this payslip to
                NPA.&nbsp;&nbsp;&nbsp;
                <span className="khmer-text">
                  កំណត់សម្គាល់​​​​៖
                  មិនតម្រូវឲប្រគាល់ច្បាប់ចម្លងដែលមានហត្ថលេខានៃបង្កាន់ដៃបើកប្រាក់បៀវត្សរ៍នេះទៅ
                  NPAទេ។
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <section className="footer">
          <div className="section-footer">
            <ul className="sec-1">
              <li>
                Phnom Penh Office #4, St. 278, Sangkat Olympic, P.O.Box
                2228,Phnom Penh, Cambodia.
              </li>
              <li>
                Phone : <span>855-23 210 383, 855-23 218 197</span>
              </li>
              <li>
                Mobile : <span>855-12 800 103, 855-12 817 217</span>
              </li>
            </ul>
            <ul className="sec-2">
              <li>
                Phnom Penh Office #4, St. 278, Sangkat Olympic, P.O.Box
                2228,Phnom Penh, Cambodia.
              </li>
              <li>
                Phone : <span>855-23 210 383, 855-23 218 197</span>
              </li>
              <li>
                Mobile : <span>855-12 800 103, 855-12 817 217</span>
              </li>
            </ul>
            <ul className="sec-3">
              <li>
                Phnom Penh Office #4, St. 278, Sangkat Olympic, P.O.Box
                2228,Phnom Penh, Cambodia.
              </li>
              <li>
                Phone : <span>855-23 210 383, 855-23 218 197</span>
              </li>
              <li>
                Mobile : <span>855-12 800 103, 855-12 817 217</span>
              </li>
            </ul>
          </div>
        </section>
      </section>
    </div>
  );
}

export default ViewPayslip;
